import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';

function ReputationConsole() {
  const [reputations, setReputations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Fields for adjustments
  const [trustScore, setTrustScore] = useState(100);
  const [reason, setReason] = useState('');
  const [role, setRole] = useState('pilgrim');
  const [actionLoading, setActionLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { 'Authorization': `Bearer ${token}` };
  }, []);

  const fetchReputations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reputation`, { headers: getAuthHeaders() });
      if (res.ok) {
        setReputations(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch reputation data', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReputations();
  }, [fetchReputations]);

  const handleAdjustReputation = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reputation/${selectedUser.user_id}/adjust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ trust_score: parseInt(trustScore), reason })
      });
      if (res.ok) {
        alert('Reputation adjusted successfully!');
        setReason('');
        setSelectedUser(null);
        fetchReputations();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail}`);
      }
    } catch (e) {
      console.error(e);
      alert('Error adjusting reputation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reputation/${selectedUser.user_id}/role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ role, reason })
      });
      if (res.ok) {
        alert('User role updated successfully!');
        setReason('');
        setSelectedUser(null);
        fetchReputations();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail}`);
      }
    } catch (e) {
      console.error(e);
      alert('Error updating user role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reputation/${selectedUser.user_id}/suspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ reason })
      });
      if (res.ok) {
        alert('User account suspended successfully!');
        setReason('');
        setSelectedUser(null);
        fetchReputations();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail}`);
      }
    } catch (e) {
      console.error(e);
      alert('Error suspending user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reputation/${selectedUser.user_id}/reactivate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        alert('User account reactivated successfully!');
        setReason('');
        setSelectedUser(null);
        fetchReputations();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail}`);
      }
    } catch (e) {
      console.error(e);
      alert('Error reactivating user');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3>Contributor Reputation Console</h3>
          <button className="btn-outline" onClick={fetchReputations}>Refresh</button>
        </div>

        {loading ? (
          <p>Loading contributors...</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User / Phone</th>
                <th>Role</th>
                <th>Trust Score</th>
                <th>Verified Alerts</th>
                <th>Rejected Alerts</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {reputations.map((r) => (
                <tr key={r.user_id}>
                  <td>
                    <strong>{r.username}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>{r.mobile_number}</div>
                  </td>
                  <td>
                    <span className="badge-info">{r.role?.toUpperCase()}</span>
                  </td>
                  <td style={{ fontWeight: 'bold', color: r.trust_score >= 100 ? '#4CAF50' : '#FF5722' }}>
                    {r.trust_score}
                  </td>
                  <td style={{ color: 'green' }}>{r.verified_count}</td>
                  <td style={{ color: 'red' }}>{r.rejected_count}</td>
                  <td>
                    <span className={r.status === 'suspended' ? 'badge-danger' : 'badge-success'}>
                      {r.status?.toUpperCase() || 'ACTIVE'}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn-outline"
                      onClick={() => {
                        setSelectedUser(r);
                        setTrustScore(r.trust_score);
                        setRole(r.role);
                        setReason('');
                      }}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
              {reputations.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center' }}>No users found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selectedUser && (
        <div className="card" style={{ border: '2px solid var(--primary)' }}>
          <h3>Manage Contributor: {selectedUser.username}</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '20px' }}>
            {/* Reputation Adjustment */}
            <div style={{ borderRight: '1px solid #eee', paddingRight: '20px' }}>
              <h4>Adjust Trust Score</h4>
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '14px' }}>Trust Score Value (10 to 200)</label>
                <input
                  type="number"
                  min="10"
                  max="200"
                  value={trustScore}
                  onChange={(e) => setTrustScore(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '14px' }}>Reason for Override</label>
                <input
                  type="text"
                  placeholder="e.g. Good feedback, Spam reports override"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
              <button 
                className="btn-primary" 
                style={{ marginTop: '16px' }}
                disabled={actionLoading}
                onClick={handleAdjustReputation}
              >
                Apply Trust Score
              </button>
            </div>

            {/* Role & Suspension */}
            <div>
              <h4>Account Status & RBAC Roles</h4>
              
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '14px' }}>System Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="pilgrim">Pilgrim</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '14px' }}>Change Role Action Reason</label>
                <input
                  type="text"
                  placeholder="e.g. Promoted to administrator role"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginTop: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button 
                  className="btn-outline"
                  disabled={actionLoading}
                  onClick={handleRoleChange}
                >
                  Save Role
                </button>

                {selectedUser.status === 'suspended' ? (
                  <button 
                    className="btn-primary"
                    style={{ background: '#4CAF50' }}
                    disabled={actionLoading}
                    onClick={handleReactivate}
                  >
                    Reactivate Account
                  </button>
                ) : (
                  <button 
                    className="btn-primary"
                    style={{ background: 'red' }}
                    disabled={actionLoading}
                    onClick={handleSuspend}
                  >
                    Suspend Account
                  </button>
                )}
                
                <button className="btn-outline" onClick={() => setSelectedUser(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReputationConsole;
